<template>
  <div>
    <div class="uk-flex uk-flex-center uk-flex-row uk-flex-wrap">
      <form v-on:submit.prevent class="uk-margin uk-form uk-grid" uk-grid>
        <div class="uk-width-1-3 uk-text-left uk-flex uk-flex-column">
          <label for="loadNumber" class="uk-form-label">Load/Doc Number</label>

          <input
            v-model="loadNumber"
            type="text"
            id="loadNumber"
            class="uk-input uk-form-width-medium uk-width-1-1"
            v-bind:class="{ 'uk-form-danger': textDanger }"
            />
        </div>

        <div class="uk-width-1-3 uk-text-left">
          <label for="driver" class="uk-form-label">Driver</label>
          <FilterableDropdown
            v-model="driverId"
            @input="driverInputChanged"
            :options="driversSelectFmtd"
            label="nameWithPhone"
            :reduce="driver => driver.id"
            class="filter-width-medium"
            taggable
            >
            <template #no-options>
              <button
                class="uk-button uk-button-default"
                type="button"
                v-on:click="showCreateDriverModal"
                >Add new driver</button>
            </template>
          </FilterableDropdown>

        </div>

        <div class="uk-width-1-3 uk-text-left">
          <label for="customer" class="uk-form-label">Customer</label>
          <FilterableDropdown
            v-model="customerId"
            @input="customerInputChanged"
            class="filter-width-medium"
            :options="customersSelectFmtd"
            label="nameWithEmail"
            :reduce="customer => customer.id"
            >
            <template #no-options>
              <button
                class="uk-button uk-button-default"
                type="button"
                v-on:click="showCreateCustomerModal"
                >Add new customer</button>
            </template>
          </FilterableDropdown>
        </div>

        <div class="uk-width-1-1 uk-margin uk-text-left">
          <a v-on:click="showOriginAddress = !showOriginAddress" class="uk-link-toggle">
            <div v-if="showOriginAddress">- Hide origin address</div>
            <div v-else>+ Add origin address (optional)</div>
          </a>
        </div>
        <AddressInputBlock v-if="showOriginAddress"
                           class="uk-margin-remove"
                           :label="'Origin Address'"
                           :address="origAddress"
                           ></AddressInputBlock>

        <div class="uk-width-1-1 uk-margin uk-text-left">
          <a v-on:click="showDestAddress = !showDestAddress" class="uk-link-toggle">
            <div v-if="showDestAddress">- Hide destination address</div>
            <div v-else>+ Add destination address (optional)</div>
          </a>
        </div>
        <AddressInputBlock v-if="showDestAddress"
                           class="uk-margin-remove"
                           :label="'Destination Address'"
                           :address="destAddress"
                           ></AddressInputBlock>

        <div class="uk-width-1-1 uk-flex-inline uk-flex-column uk-text-left">
          <!-- Need to do this so that button is same height as input fields, so this makes a label with whitespace -->
          <label class="uk-form-label">{{'\xa0'}}</label>
          <button class="uk-button uk-button-primary uk-form-controls" v-on:click="addNewLoad">
            <div uk-icon="icon: plus"></div>Create Load
          </button>
        </div>
      </form>
    </div>

    <div v-if="loadingLoad">
      <div class="uk-alert-primary" uk-alert>
        <!-- <p>Saving load details for load {{loadNumber}}</p> -->
        <div uk-spinner></div>
      </div>
    </div>

    <div v-if="loadFormError" class="uk-form-danger">
      <p>{{ loadFormError }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import FilterableDropdown from '../core/FilterableDropdown.vue';
import AddressInputBlock from '../core/AddressInputBlock.vue';

import CREATE_POD_LOAD from '../../gql/pod_tracking/CreatePODLoad.gql';
// import CREATE_POD_LOAD_FROM_PRIMITIVES from '../../gql/pod_tracking/CreatePODLoadFromPrimitives.gql';

import { Address } from '../../types';


interface Props {
  drivers: any[];
}

interface Data {
  loadNumber: string;
  driverPhone: string;
  customerName: string;
  loadFormError: any;
  textDanger: boolean;
  loadingLoad: boolean;
}

interface Computed {
  driversNames: string[];
}

interface Methods {
  addNewLoad: () => void;
  getAllDrivers: () => void;
  getAllCustomers: () => void;
}

const ADD_DRIVER_OPTION = '+ Add New Driver';
const ADD_CUSTOMER_OPTION = '+ Add New Customer';

// export default Vue.extend<Data, Methods, Computed, Props>({
export default defineComponent({
  name: 'EnterLoad',
  // name: 'EnterLoad',
  components: {
    FilterableDropdown,
    AddressInputBlock,
  },
  emits: ['addedLoad', 'createdDriver', 'createdCustomer', 'openCreateDriver', 'openCreateCustomer'],
  props: {
    drivers: {
      required: true,
      type: Array,
    },
    customers: {
      required: true,
      type: Array,
    },
  },
  data() {
    return {
      loadNumber: null,
      driverId: null,
      customerId: null,

      origAddress: {} as Address,
      destAddress: {} as Address,

      showOriginAddress: false,
      showDestAddress: false,

      loadFormError: null,
      textDanger: false,
      loadingLoad: false,
    };
  },
  computed: {
    // Adding a field called 'nameWithPhone' to the drivers list so that the
    //   name and phone is displayed in the select
    // TODO: can remove this by just using a slot to format how the UI looks on the v-select component
    driversSelectFmtd() {
      const vm = (this as any);

      const driversWithNamePhone = vm.drivers.map((d) => {
        const newD = { ...d};
        const nameWithPhone = `${(d as any).name || ''} (${
          (d as any).phoneNumber
        })`;
        (newD as any).nameWithPhone = nameWithPhone;
        return newD;
      });
      console.log('new drivers: ', driversWithNamePhone);

      const createDriverOption = {
        nameWithPhone: ADD_DRIVER_OPTION,
        phoneNumber: ADD_DRIVER_OPTION,
        id: ADD_DRIVER_OPTION,
      };

      // .sort();
      return [createDriverOption].concat(driversWithNamePhone as Array<any>);
    },
    // Adding a field called 'nameWithEmail' to the customers list so that the
    //   name and email is displayed in the select
    // TODO: can remove this by just using a slot to format how the UI looks on the v-select component
    customersSelectFmtd() {
      const vm = (this as any);

      console.log('this.customers: ', vm.customers);
      const customersWithNameEmail = vm.customers.map((d) => {
        const newD = { ...d };
        const nameWithEmail = `${(d as any).name} (${
          (d as any).contactEmail
        })`;
        (newD as any).nameWithEmail = nameWithEmail;
        return newD;
      });
      console.log('new customers: ', customersWithNameEmail);

      const createCustomerOption = {
        nameWithEmail: ADD_CUSTOMER_OPTION,
        phoneNumber: ADD_CUSTOMER_OPTION,
        id: ADD_CUSTOMER_OPTION,
      };

      // .sort();
      return [createCustomerOption].concat(customersWithNameEmail as Array<any>);
    },
  },
  watch: {
    // loadNumber(val) {
    //   if (val !== '') {
    //     this.loadNumber = (this as any).$options.filters.uppercase(val);

    //     return;
    //   }
    //   this.loadNumber = val;
    // },
  },
  methods: {
    toUppercase(value: string): string {
      if (!value) return '';
      return value.toUpperCase();
    },
    driverInputChanged(val) {
      const vm = (this as any);

      console.log(`\n\ndriver phone changed: ${val}`);

      // User clicked on adding a new driver
      if (val === ADD_DRIVER_OPTION) {
        // (this as any).driverPhone = 'fds';
        vm.showCreateDriverModal();
        vm.driverId = null;
      }
    },
    customerInputChanged(val) {
      const vm = (this as any);

      console.log(`\n\ncustomer phone changed: ${val}`);

      // User clicked on adding a new customer
      if (val === ADD_CUSTOMER_OPTION) {
        vm.showCreateCustomerModal();
        vm.customerId = null;
      }
    },
    showCreateDriverModal() {
      (this as any).$emit('openCreateDriver');
    },
    showCreateCustomerModal() {
      (this as any).$emit('openCreateCustomer');
    },
    createdDriver() {
      const vm = (this as any);

      vm.$emit('createdDriver');
    },
    createdCustomer() {
      const vm = (this as any);

      vm.$emit('createdCustomer');
    },
    addNewLoad() {
      const vm = (this as any);

      console.log('--- START ADDING LOAD ---');
      console.log('Load Number:', vm.loadNumber);
      console.log('Driver ID:', vm.driverId, typeof vm.driverId);
      console.log('Customer ID:', vm.customerId, typeof vm.customerId);
      console.log('Orig Address:', vm.origAddress);

      if (!vm.loadNumber || !vm.driverId || !vm.customerId) {
        console.warn('Validation Failed: Missing required fields');
        vm.loadFormError = 'Error: Fill out all fields (Load #, Driver, Customer)';
        return;
      }

      vm.loadingLoad = true;


      const variables: any = {
        loadDocumentId: vm.loadNumber,
        driverId: parseInt(vm.driverId),
        customerId: parseInt(vm.customerId),
      };


      if (vm.origAddress && Object.keys(vm.origAddress).length > 0 && vm.origAddress.streetAddress) {
         variables.inputOriginAddress = vm.origAddress;
      }
      
      if (vm.destAddress && Object.keys(vm.destAddress).length > 0 && vm.destAddress.streetAddress) {
         variables.inputDestAddress = vm.destAddress;
      }
      
      if (vm.origAddress && Object.keys(vm.origAddress).length > 0) {
        variables.origAddress = vm.origAddress; 
      }
      if (vm.destAddress && Object.keys(vm.destAddress).length > 0) {
        variables.destAddress = vm.destAddress;
      }

      console.log('Sending Mutation with variables:', variables);

      vm.$apollo
        .mutate({
          mutation: CREATE_POD_LOAD,
          variables: variables,
        })
        // eslint-disable-next-line
        .then((data) => {
          console.log('Mutation Success:', data);
          vm.loadFormError = null;
          vm.textDanger = false;
          vm.loadingLoad = false;
          
          vm.loadNumber = null;
          vm.driverId = null;
          vm.customerId = null;
          vm.origAddress = {};
          vm.destAddress = {};

          vm.$emit('addedLoad');

          window.alert('Load created successfully!');
        })
        .catch((error) => {
          console.error('Mutation Error:', error);
          vm.loadFormError = error.message;
          vm.loadingLoad = false;
        });
    },
    getAllDrivers() {
      return null;
    },
    getAllCustomers() {
      return null;
    },
  },
});
</script>
